import { Injectable } from '@angular/core'
import { from, Observable } from 'rxjs'
import { switchMap } from 'rxjs/operators'
import { ExcludedAreasResponse, GenericCoordinates, GEOSnapToRoadsRequest, GEOSnapToRoadsResponse, LocationClubResponse } from './location.types'
import { HighwayExits } from './location.types'
import { DrrHttpClient } from '../../http/drr-http-client'
import { ExcludedAreasResponseSchema } from './location.model'

/**
 * DrrHttpClient feeds query params straight into `new HttpParams({ fromObject })`, which only
 * accepts strings. The coordinate payloads reaching these endpoints carry numbers (and an
 * optional zipcode that may be undefined), so every defined value is stringified and the empty
 * ones dropped — matching how Axios used to omit null/undefined params.
 */
const toQueryParams = (source: object): Record<string, string> =>
  Object.entries(source ?? {}).reduce((params, [key, value]) => {
    if (value !== undefined && value !== null) {
      params[key] = String(value)
    }
    return params
  }, {} as Record<string, string>)

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(private httpClient: DrrHttpClient) {}

  getHighwayExits$(coordinates: GenericCoordinates): Observable<HighwayExits> {
    return this.httpClient.get<HighwayExits>('bff/roadservice/highway/exits', {
      params: toQueryParams(coordinates),
    })
  }

  getClubByLocation$(coordinates: GenericCoordinates): Observable<LocationClubResponse> {
    return this.httpClient.get<LocationClubResponse>('bff/roadservice/club', {
      params: toQueryParams(coordinates),
    })
  }

  snapToRoads$(request: GEOSnapToRoadsRequest): Observable<GEOSnapToRoadsResponse> {
    return this.httpClient.post<GEOSnapToRoadsResponse>('bff/roadservice/geo/snap-to-roads', request)
  }

  getExcludedAreas$(coordinates: GenericCoordinates): Observable<ExcludedAreasResponse> {
    return this.httpClient.get<ExcludedAreasResponse>('/bff/roadservice/excluded-areas', {
      params: {
        latitude: coordinates.latitude.toString(),
        longitude: coordinates.longitude.toString(),
      },
    }).pipe(
      switchMap((response) => from(ExcludedAreasResponseSchema.validate(response)))
    )
  }
}
